'use client';

import React from 'react';
import PropTypes from 'prop-types';
import Radio from '@nib-components/radio';

const validFormModes = ['light', 'white'] as const;
type FormModes = (typeof validFormModes)[number];

type Label = string | React.ReactNode;
type ObjectOption = {label: Label; subtitle?: string | null};

export interface RadioGroupProps {
  id?: string;
  name?: string;
  value?: string | number;
  options: Record<string, Label | ObjectOption>; // This type means that all options must be of the same type, cannot mix-n-match
  component?: any;
  boldLabel?: boolean;
  disabled?: boolean;
  valid?: boolean;
  validated?: boolean;
  formMode?: FormModes;
  inline?: boolean;
  isCompact?: boolean;
  [key: string]: unknown;
}

const isObjectOptions = (option: Label | ObjectOption): option is ObjectOption => {
  return Boolean((option as ObjectOption).label);
};

export default function RadioGroup(props: RadioGroupProps): JSX.Element {
  const {name, id, options, value, disabled, component: Component = Radio, boldLabel, ...otherProps} = props;
  const hasSubtitle = Object.values(options).some(option => (option as ObjectOption)?.subtitle);
  return (
    <React.Fragment>
      {Object.keys(options).map(option => {
        const currentOption = options[option];

        if (isObjectOptions(currentOption)) {
          return (
            <Component
              {...otherProps}
              key={option}
              name={name}
              id={`${id}-${option}`}
              value={option}
              selected={option === value}
              disabled={disabled}
              label={currentOption.label}
              subtitle={currentOption.subtitle}
              boldLabel={boldLabel || hasSubtitle}
            />
          );
        }

        return (
          <Component
            {...otherProps}
            key={option}
            name={name}
            id={`${id}-${option}`}
            value={option}
            selected={option === value}
            disabled={disabled}
            label={currentOption}
            boldLabel={boldLabel || hasSubtitle}
          />
        );
      })}
    </React.Fragment>
  );
}

RadioGroup.displayName = 'RadioGroup';

const labelPropType = PropTypes.node.isRequired;

RadioGroup.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  options: PropTypes.oneOfType([
    PropTypes.objectOf(labelPropType),
    PropTypes.objectOf(
      PropTypes.shape({
        label: labelPropType,
        subtitle: PropTypes.string
      }).isRequired
    )
  ]).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  boldLabel: PropTypes.bool,
  disabled: PropTypes.bool,
  component: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
};

RadioGroup.defaultProps = {
  component: Radio
};
